import { Behavior } from "../Behavior";
import type { IGrid } from "../types";
import type { Particle } from "../Particle";
import { Empty } from "../particles/Empty";

export class MovesUp extends Behavior {
    update(particle: Particle, grid: IGrid, index: number) {
        // 重置速度，避免惯性累积导致“散开”
        // 我们希望气体像 Cellular Automata 那样移动，而不是物理粒子
        particle.velocity.x = 0;
        particle.velocity.y = 0;

        const gx = grid.gravity.x;
        const gy = grid.gravity.y;
        
        // 计算目标移动方向（反重力）
        // 添加一点点随机扰动，模拟气体的布朗运动，但非常微小
        // 关键是：不累积速度
        
        const spread = 0.5; // 扩散倾向
        const dx = -gx + (Math.random() - 0.5) * spread;
        const dy = -gy + (Math.random() - 0.5) * spread;
        
        // 归一化方向，每次只移动一步
        // 这样可以保证紧凑性
        // 如果重力很小，可能不移动
        if (Math.abs(gx) < 0.1 && Math.abs(gy) < 0.1) {
             // 无重力时随机游走
             if (Math.random() < 0.1) {
                 const randIdx = this.randomWalk(grid, index);
                 return randIdx;
             }
             return index;
        }

        let cx = index % grid.width;
        let cy = Math.floor(index / grid.width);

        // 尝试主方向移动
        const tx = Math.round(cx + dx);
        const ty = Math.round(cy + dy);
        
        if (tx >= 0 && tx < grid.width && ty >= 0 && ty < grid.height) {
            const tIdx = ty * grid.width + tx;
            const target = grid.pixels[tIdx];
            
            if (this.canDisplace(target)) {
                grid.swap(index, tIdx);
                return tIdx;
            }
        }
        
        // 如果受阻，尝试侧滑（相对于重力）
        return this.diffuse(grid, index, cx, cy, gx, gy);
    }
    
    canDisplace(p: Particle) {
        return p.empty || p.props.isLiquid || p.props.isPowder;
    }
    
    randomWalk(grid: IGrid, index: number) {
         const cx = index % grid.width;
         const cy = Math.floor(index / grid.width);
         const dx = Math.floor(Math.random() * 3) - 1;
         const dy = Math.floor(Math.random() * 3) - 1;
         if (dx === 0 && dy === 0) return index;
         const tx = cx + dx;
         const ty = cy + dy;
         if (tx >= 0 && tx < grid.width && ty >= 0 && ty < grid.height) {
             const tIdx = ty * grid.width + tx;
             if (this.canDisplace(grid.pixels[tIdx])) {
                 grid.swap(index, tIdx);
                 return tIdx;
             }
         }
         return index;
    }
    
    diffuse(grid: IGrid, index: number, cx: number, cy: number, gx: number, gy: number): number {
        // 随机左右尝试
        // 侧向向量：(-gy, gx) 和 (gy, -gx)
        const dirs = Math.random() > 0.5 ? [1, -1] : [-1, 1];
        
        for (const side of dirs) {
            // 稍微偏向上方的侧向
            // 原始逻辑通常是：如果正上方堵住，查左上和右上
            // 这里的“上”是 (-gx, -gy)
            // 这里的“左/右”是 (-gy, gx) * side
            // 混合向量：Up + Side
            
            const upX = -gx;
            const upY = -gy;
            
            const sideX = -gy * side;
            const sideY = gx * side;
            
            // 组合：朝斜上方移动
            const tx = Math.round(cx + upX + sideX);
            const ty = Math.round(cy + upY + sideY);
            
            if (tx < 0 || tx >= grid.width || ty < 0 || ty >= grid.height) continue;
            
            const tIdx = ty * grid.width + tx;
            const target = grid.pixels[tIdx];
            
            if (this.canDisplace(target)) {
                grid.swap(index, tIdx);
                return tIdx;
            }
        }
        return index;
    }
}
